/**
 * LocalStorage utility functions with error handling
 */

/**
 * Safely get an item from localStorage
 */
export function getLocalStorageItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.error(`Failed to get localStorage item "${key}":`, error);
    return null;
  }
}

/**
 * Safely set an item in localStorage
 */
export function setLocalStorageItem(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    console.error(`Failed to set localStorage item "${key}":`, error);
    return false;
  }
}

/**
 * Safely remove an item from localStorage
 */
export function removeLocalStorageItem(key: string): boolean {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error(`Failed to remove localStorage item "${key}":`, error);
    return false;
  }
}

/**
 * Safely parse JSON from localStorage
 */
export function getLocalStorageJSON<T>(key: string, defaultValue: T): T {
  try {
    const stored = localStorage.getItem(key);
    if (stored) {
      return JSON.parse(stored) as T;
    }
  } catch (error) {
    console.error(`Failed to parse localStorage JSON "${key}":`, error);
  }
  return defaultValue;
}

/**
 * Safely stringify and store JSON in localStorage
 */
export function setLocalStorageJSON<T>(key: string, value: T): boolean {
  try {
    const serialized = JSON.stringify(value);
    localStorage.setItem(key, serialized);
    return true;
  } catch (error) {
    console.error(`Failed to stringify and store JSON "${key}":`, error);
    return false;
  }
}

/**
 * Clear all items from localStorage
 */
export function clearAllLocalStorage(): void {
  try {
    localStorage.clear();
  } catch (error) {
    console.error('Failed to clear localStorage:', error);
  }
}

/**
 * Clear all items from sessionStorage
 * 
 * @param preserveSecureSessionId - If true, preserve secure_session_id (for partial clears).
 *                                   Default false for logout (complete clear).
 */
export function clearAllSessionStorage(preserveSecureSessionId: boolean = false): void {
  try {
    if (preserveSecureSessionId) {
      // Preserve secure_session_id to maintain encryption key consistency
      // If we clear it, any remaining encrypted data will be unreadable
      const sessionId = sessionStorage.getItem('secure_session_id');
      sessionStorage.clear();
      // Restore session ID if it existed, so encrypted data can still be decrypted
      if (sessionId) {
        sessionStorage.setItem('secure_session_id', sessionId);
      }
    } else {
      // Complete clear (for logout)
      sessionStorage.clear();
    }
  } catch (error) {
    console.error('Failed to clear sessionStorage:', error);
  }
}

